import { Form as AntForm, Form } from "antd";
import { useAppDispatch } from "core/utils";
import { isEqual } from "lodash";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import type { Entity } from "../../core/types/fe/Entity";
import { showWarningNotification } from "../NotificationAlerts";
import { DefaultFormLayout } from "./layouts/DefaultFormLayout";
import { InlineFormLayout } from "./layouts/InlineFormLayout";
import { TabsFormLayout } from "./layouts/TabsFormLayout";
import type { FormManagerProps } from "./types";
import { FormLayoutType } from "./types";
import type { FormError } from "./types/types";

// Map with the available layouts
const Layouts = {
    [FormLayoutType.DEFAULT]: DefaultFormLayout,
    [FormLayoutType.TABS]: TabsFormLayout,
    [FormLayoutType.INLINE]: InlineFormLayout,
};

export const FormManager = <T extends Entity>(props: FormManagerProps<T>) => {
    useTranslation();
    const { customFormInstance, name, formData, onChange, formLayout, formDefinition, shouldValidate, onValidated } = props;
    const dispatch = useAppDispatch();

    const [form] = AntForm.useForm(customFormInstance);

    useEffect(() => {
        const currentValues = form.getFieldsValue();
        if (!isEqual(currentValues, formData)) {
            form.setFieldsValue(formData);
        }
    }, [formData, form]);
    const onFormChange = useCallback(
        (value: T) => {
            onChange(value);
        },
        [onChange],
    );

    const [runValidation, setRunValidation] = useState(false);
    useEffect(() => {
        if (!shouldValidate) {
            return;
        }

        setRunValidation(true);
    }, [shouldValidate, setRunValidation]);

    useEffect(() => {
        if (!onValidated || !runValidation) {
            return;
        }

        form.validateFields()
            .then(() => {
                onValidated(true);
            })
            .catch((error: FormError) => {
                onValidated(false);
                dispatch(
                    showWarningNotification({
                        text: `There are ${error.errorFields.length} validation errors:
                        ${error.errorFields.map((field) => `\n ${field.name.join(".")}: ${field.errors.join("; ")}`).join("")}`,
                        autoCloseDisabled: true,
                    }),
                );
            });

        setRunValidation(false);
    }, [dispatch, form, onValidated, runValidation, setRunValidation]);

    const layoutFactory = useCallback(
        // @ts-ignore
        () => React.createElement(Layouts[formLayout], { formDefinition, formData, onChange }),
        [formLayout, formDefinition, formData, onChange],
    );

    // Render
    return (
        <AntForm
            data-name={name}
            initialValues={formData}
            form={form}
            name={name}
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            onValuesChange={(changedValues, values) => onFormChange(values)}
            autoComplete="off"
        >
            {layoutFactory()}
            {formData?.id && (
                <Form.Item key={`${formData.id}-Id`} name="id" hidden>
                    <input type="hidden" value={formData.id} />
                </Form.Item>
            )}
        </AntForm>
    );
};
